import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { canAccess } from '../utils/canAccess';
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import * as XLSX from 'xlsx';

import { FaOilCan, FaHistory, FaPlus, FaSyncAlt, FaFilePdf, FaSearch, FaFileExcel } from 'react-icons/fa';
import { FaLayerGroup, FaCheckCircle, FaExclamationTriangle, FaFireAlt, FaRoad, FaClock } from 'react-icons/fa';
import { FaCircleStop } from 'react-icons/fa6';

import TableComponentVidange from '../components/TableComponentVidange';
import VidangeHistoriqueTable from '../components/VidangeHistoriqueTable';
import ConfirmModal from '../components/ConfirmModal';
import { generateVidangePDF } from '../utils/vidangePDF';
import Badge from 'react-bootstrap/Badge';

import { normalizeModeCompteur } from '../utils/normalize';
import '../styles/VidangePage.css';

const normalizeSoft = (v) =>
  (v || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatDateDisplay = (value) => {
  if (!value) return '—';
  return value;
};

const formatNumberDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n;
};

const formatCompteurDisplay = (value, mode) => {
  const n = formatNumberDisplay(value);
  if (n === '—') return n;
  const unit = normalizeModeCompteur(mode) === 'km' ? 'km' : 'h';
  return `${n} ${unit}`;
};

const sanitizeFileName = (value) => {
  return (value || 'export')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_');
};

const VidangePage = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);

  const [suivi, setSuivi] = useState([]);
  const [chantiers, setChantiers] = useState([]);

  const [filterChantierName, setFilterChantierName] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterSociete, setFilterSociete] = useState(['RUTAS', 'PISTAS', 'CADEX']);
  const [filterModeCompteur, setFilterModeCompteur] = useState('');

  const [histStart, setHistStart] = useState(todayISO());
  const [histEnd, setHistEnd] = useState(todayISO());
  const [histMaterielId, setHistMaterielId] = useState(null);
  const [historique, setHistorique] = useState([]);

  const [showAdd, setShowAdd] = useState(false);
  const [addMaterielId, setAddMaterielId] = useState(null);
  const [addDate, setAddDate] = useState(todayISO());
  const [addObs, setAddObs] = useState('');

  const [showKpiModal, setShowKpiModal] = useState(false);
  const [kpiModalTitle, setKpiModalTitle] = useState('');
  const [kpiModalRows, setKpiModalRows] = useState([]);
  const [kpiModalType, setKpiModalType] = useState('TOTAL');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);

  const isAdmin = user?.role !== 'pointeur' && canAccess(user, 'vidanges:delete');

  const handleRecalculate = async () => {

    try {
      setLoading(true);
      const res = await axios.post('/materiel-vidanges/recalculate-counters');

      setSuccess(res?.data?.message || '✅ Recalcul terminé.');
      setTimeout(() => setSuccess(''), 3000);

      await reloadSuivi();
    } catch (err) {
      console.error('❌ handleRecalculate', err);
      setError(err?.response?.data?.message || '❌ Erreur lors du recalcul des compteurs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInit = async () => {
    setError('');
    setSuccess('');

    try {
      setLoading(true);

      const [suiviRes, chantierRes] = await Promise.all([
        axios.get('/materiel-vidanges/suivi'),
        axios.get('/chantiers')
      ]);

      setSuivi(suiviRes?.data?.data || []);
      setChantiers(chantierRes?.data || []);
    } catch (e) {
      console.error('❌ loadInit VidangePage', e);
      setError("❌ Erreur lors du chargement des données vidange.");
    } finally {
      setLoading(false);
    }
  };

  const reloadSuivi = async () => {
    setError('');
    setSuccess('');

    try {
      setLoading(true);

      const suiviRes = await axios.get('/materiel-vidanges/suivi');
      setSuivi(suiviRes?.data?.data || []);

      setSuccess('✅ Suivi vidange actualisé.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      console.error('❌ reloadSuivi', e);
      setError("❌ Impossible d'actualiser le suivi vidange.");
    } finally {
      setLoading(false);
    }
  };

  const loadHistorique = async () => {
    setError('');
    setSuccess('');

    try {
      setLoadingHist(true);

      const params = new URLSearchParams();
      if (histStart) params.append('start', histStart);
      if (histEnd) params.append('end', histEnd);
      if (histMaterielId) params.append('materiel_id', String(histMaterielId));

      const res = await axios.get(`/materiel-vidanges?${params.toString()}`);
      setHistorique(res?.data?.data || []);

      setSuccess(`✅ Historique chargé (${(res?.data?.data || []).length} ligne(s)).`);
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      console.error('❌ loadHistorique', e);
      setError("❌ Erreur lors du chargement de l'historique des vidanges.");
    } finally {
      setLoadingHist(false);
    }
  };

  const openAddModal = () => {
    setAddMaterielId(null);
    setAddDate(todayISO());
    setAddObs('');
    setShowAdd(true);
  };

  const handleCreateVidange = async () => {
    setError('');
    setSuccess('');

    if (!addMaterielId) {
      setError('⚠️ Merci de sélectionner un matériel.');
      return;
    }

    if (!addDate) {
      setError('⚠️ Merci de choisir la date de vidange.');
      return;
    }

    try {
      setLoading(true);

      await axios.post('/materiel-vidanges', {
        materiel_id: addMaterielId,
        date_vidange: addDate,
        observation: addObs
      });

      setShowAdd(false);
      setSuccess('✅ Vidange enregistrée + compteur remis à zéro.');
      setTimeout(() => setSuccess(''), 2500);

      await reloadSuivi();
      await loadHistorique();
    } catch (e) {
      console.error('❌ handleCreateVidange', e);
      setError("❌ Erreur lors de l'enregistrement de la vidange.");
    } finally {
      setLoading(false);
    }
  };

  const chantierOptions = useMemo(() => {
    return (chantiers || []).map((c) => ({
      value: c.name,
      label: c.name
    }));
  }, [chantiers]);

  const statutOptions = useMemo(() => {
    return [
      { value: '', label: 'Tous' },
      { value: 'OK', label: '✅ OK' },
      { value: 'ALERTE', label: '⚠️ Alerte' },
      { value: 'CRITIQUE', label: '🔥 Critique' },
      { value: 'DEPASSE', label: '🛑 Dépassé' }
    ];
  }, []);

  const modeOptions = useMemo(() => {
    return [
      { value: '', label: 'Tous' },
      { value: 'h', label: '⏱️ Heures' },
      { value: 'km', label: '🛣️ Kilomètres' }
    ];
  }, []);

  const societeOptions = useMemo(() => {
    const values = [...new Set((suivi || []).map((m) => m?.societe).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));

    return values.map((s) => ({
      value: s,
      label: s
    }));
  }, [suivi]);

  const materielOptions = useMemo(() => {
    return (suivi || [])
      .filter((m) => normalizeSoft(m?.etat) !== 'bloque')
      .map((m) => {
        const mode = normalizeModeCompteur(m?.mode_compteur);
        const unit = mode === 'km' ? 'km' : 'h';
        return {
          value: m.id,
          label: `#${m.id} — ${m.matricule || 'Sans matricule'} (${m.chantier_actuel || 'Chantier ?'}) • ${unit}`
        };
      });
  }, [suivi]);

  const suiviFiltered = useMemo(() => {
    const chantierFilterNorm = normalizeSoft(filterChantierName);
    const statutFilter = filterStatut;
    const modeFilter = normalizeModeCompteur(filterModeCompteur || '');

    return (suivi || []).filter((row) => {
      const chantierActuelNorm = normalizeSoft(row.chantier_actuel || '');
      const statutCode = row?.statut?.code || '';
      const societeNorm = normalizeSoft(row?.societe || '');
      const mode = normalizeModeCompteur(row?.mode_compteur);

      const okChantier =
        !chantierFilterNorm ||
        chantierActuelNorm === chantierFilterNorm ||
        chantierActuelNorm.includes(chantierFilterNorm) ||
        chantierFilterNorm.includes(chantierActuelNorm);

      const okStatut = !statutFilter || statutCode === statutFilter;

      const okSociete =
        !filterSociete.length ||
        filterSociete.some((s) => societeNorm.includes(normalizeSoft(s)));

      const okMode = !filterModeCompteur || mode === modeFilter;

      return okChantier && okStatut && okSociete && okMode;
    });
  }, [suivi, filterChantierName, filterStatut, filterSociete, filterModeCompteur]);

  const filteredKpi = useMemo(() => {
    return (suiviFiltered || []).reduce(
      (acc, row) => {
        const code = row?.statut?.code || '';
        const mode = normalizeModeCompteur(row?.mode_compteur);

        acc.total += 1;
        if (mode === 'km') acc.modeKm += 1;
        else acc.modeH += 1;

        if (code === 'OK') acc.ok += 1;
        else if (code === 'ALERTE') acc.alerte += 1;
        else if (code === 'CRITIQUE') acc.critique += 1;
        else if (code === 'DEPASSE') acc.depasse += 1;

        return acc;
      },
      { total: 0, ok: 0, alerte: 0, critique: 0, depasse: 0, modeH: 0, modeKm: 0 }
    );
  }, [suiviFiltered]);

  const openKpiModal = (type) => {
    let rows = [];
    let title = '';

    switch (type) {
      case 'TOTAL':
        rows = suiviFiltered;
        title = '📋 Détail KPI — Tous les matériels filtrés';
        break;
      case 'OK':
        rows = suiviFiltered.filter((r) => r?.statut?.code === 'OK');
        title = '✅ Détail KPI — Matériels OK';
        break;
      case 'ALERTE':
        rows = suiviFiltered.filter((r) => r?.statut?.code === 'ALERTE');
        title = '⚠️ Détail KPI — Matériels en alerte';
        break;
      case 'CRITIQUE':
        rows = suiviFiltered.filter((r) => r?.statut?.code === 'CRITIQUE');
        title = '🔥 Détail KPI — Matériels critiques';
        break;
      case 'DEPASSE':
        rows = suiviFiltered.filter((r) => r?.statut?.code === 'DEPASSE');
        title = '🛑 Détail KPI — Matériels dépassés';
        break;
      case 'MODE_H':
        rows = suiviFiltered.filter((r) => normalizeModeCompteur(r?.mode_compteur) === 'h');
        title = '⏱️ Détail KPI — Matériels suivis en heures';
        break;
      case 'MODE_KM':
        rows = suiviFiltered.filter((r) => normalizeModeCompteur(r?.mode_compteur) === 'km');
        title = '🛣️ Détail KPI — Matériels suivis en kilomètres';
        break;
      default:
        rows = [];
        title = '📋 Détail KPI';
        break;
    }

    setKpiModalRows(rows);
    setKpiModalTitle(title);
    setKpiModalType(type);
    setShowKpiModal(true);
  };

  const exportSuiviPDF = () => {
    try {
      const rows = (suiviFiltered || []).map((r) => {
        const mode = normalizeModeCompteur(r.mode_compteur);
        return {
          id: r.id,
          matricule: r.matricule,
          societe: r.societe,
          type: r.type || '',
          chantier_actuel: r.chantier_actuel,
          date_vidange: r.date_vidange,
          mode_compteur: mode,
          compteur_vidange_h: r.compteur_vidange_h,
          interval_vidange_h: r.interval_vidange_h,
          seuil_alerte_h: r.seuil_alerte_h,
          seuil_critique_h: r.seuil_critique_h,
          reste_h: r.reste_h,
          compteur_vidange_km: r.compteur_vidange_km,
          interval_vidange_km: r.interval_vidange_km,
          seuil_alerte_km: r.seuil_alerte_km,
          seuil_critique_km: r.seuil_critique_km,
          reste_km: r.reste_km,
          cycle_value: r.cycle_value,
          interval_value: r.interval_value,
          reste_value: r.reste_value,
          statut: r?.statut?.label || ''
        };
      });

      generateVidangePDF({
        title: 'Suivi Vidange – Matériels',
        userName: user?.login || user?.username || user?.name || '',
        date: new Date().toLocaleString(),
        rows,
        filters: {
          chantier: filterChantierName || 'Tous',
          statut: filterStatut || 'Tous',
          societe: filterSociete.length ? filterSociete.join(' / ') : 'Toutes',
          mode_compteur: filterModeCompteur || 'Tous'
        }
      });
    } catch (e) {
      console.error('❌ exportSuiviPDF', e);
      setError('❌ Erreur export PDF.');
    }
  };

  const exportKpiModalExcel = () => {
    try {
      const rows = (kpiModalRows || []).map((row) => {
        const mode = normalizeModeCompteur(row.mode_compteur);
        const unit = mode === 'km' ? 'km' : 'h';

        return {
          ID: row.id,
          Matricule: row.matricule || '',
          Type: row.type || '',
          Societe: row.societe || '',
          Mode: mode,
          Unite: unit,
          Dernier_Chantier: row.chantier_actuel || '',
          Date_Derniere_Vidange: row.date_vidange || '',
          Cycle_Actuel: row.cycle_value ?? '',
          Intervalle: row.interval_value ?? '',
          Reste: row.reste_value ?? '',
          Statut: row?.statut?.label || row?.statut?.code || ''
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Detail KPI');

      const fileName = sanitizeFileName(
        `detail_kpi_${kpiModalType}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      XLSX.writeFile(workbook, fileName);
    } catch (e) {
      console.error('❌ exportKpiModalExcel', e);
      setError("❌ Erreur lors de l'export Excel du détail KPI.");
    }
  };

  const renderClickableKpiCard = ({ className, label, value, hint, icon, onClick }) => (
    <div
      className={`vd-kpi-card ${className}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
      title="Cliquer pour voir le détail"
    >
      <div className="vd-kpi-top">
        <div>
          <div className="vd-kpi-label">{label}</div>
          <div className="vd-kpi-value">{value}</div>
          <div className="vd-kpi-hint">{hint}</div>
        </div>
        <div className="vd-kpi-icon">{icon}</div>
      </div>
    </div>
  );

  return (
    <div className="container mt-3 vd-page">
      <Card className="vd-card">
        <Card.Body>
          <div className="vd-header">
            <div>
              <h4 className="vd-title">
                <FaOilCan className="me-2" />
                Gestion des Vidanges 🛢️
              </h4>
              <div className="vd-subtitle">
                Suivi dynamique Heures / KM + Historique + Ajout vidange.
              </div>
            </div>

            <div className="vd-actions d-flex gap-2 flex-wrap">
              <Button variant="secondary" onClick={reloadSuivi} disabled={loading}>
                <FaSyncAlt className="me-2" />
                Actualiser
              </Button>

              {isAdmin && (
                <Button variant="dark" size="sm" onClick={() => setShowRecalcConfirm(true)} disabled={loading}>
                  🔄 Recalculer compteurs
                </Button>
              )}

              <Button variant="success" onClick={openAddModal} disabled={loading}>
                <FaPlus className="me-2" />
                Nouvelle Vidange
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          {success && <Alert variant="success" className="mt-3">{success}</Alert>}

          <div className="mt-2">
            <Alert variant="info" className="mb-2">
              🎯 Filtre société actif par défaut : <b>RUTAS / PISTAS / CADEX</b>
            </Alert>
          </div>

          <div className="mt-3 vd-kpi-grid">
            {renderClickableKpiCard({
              className: 'kpi-total',
              label: 'Total',
              value: filteredKpi.total,
              hint: 'Nombre d’engins suivis',
              icon: <FaLayerGroup />,
              onClick: () => openKpiModal('TOTAL')
            })}

            {renderClickableKpiCard({
              className: 'kpi-ok',
              label: 'OK',
              value: filteredKpi.ok,
              hint: 'Sous seuil alerte',
              icon: <FaCheckCircle />,
              onClick: () => openKpiModal('OK')
            })}

            {renderClickableKpiCard({
              className: 'kpi-alerte',
              label: 'Alerte',
              value: filteredKpi.alerte,
              hint: 'À planifier bientôt',
              icon: <FaExclamationTriangle />,
              onClick: () => openKpiModal('ALERTE')
            })}

            {renderClickableKpiCard({
              className: 'kpi-critique',
              label: 'Critique',
              value: filteredKpi.critique,
              hint: 'Intervention urgente',
              icon: <FaFireAlt />,
              onClick: () => openKpiModal('CRITIQUE')
            })}

            {renderClickableKpiCard({
              className: 'kpi-depasse',
              label: 'Dépassé',
              value: filteredKpi.depasse,
              hint: 'Interval dépassé',
              icon: <FaCircleStop />,
              onClick: () => openKpiModal('DEPASSE')
            })}

            {renderClickableKpiCard({
              className: 'kpi-ok',
              label: 'Mode Heures',
              value: filteredKpi.modeH,
              hint: 'Suivi en heures',
              icon: <FaClock />,
              onClick: () => openKpiModal('MODE_H')
            })}

            {renderClickableKpiCard({
              className: 'kpi-total',
              label: 'Mode KM',
              value: filteredKpi.modeKm,
              hint: 'Suivi en kilomètres',
              icon: <FaRoad />,
              onClick: () => openKpiModal('MODE_KM')
            })}
          </div>

          {isAdmin && (
            <div className="mt-2 text-end">
              <Button variant="outline-dark" size="sm" onClick={exportSuiviPDF}>
                <FaFilePdf className="me-2" />
                Export PDF
              </Button>
            </div>
          )}

          <Row className="mt-3 g-2">
            <Col md={3}>
              <Form.Label className="fw-bold" style={{ color: '#6d4e36' }}>
                🏗️ Filtre Chantier
              </Form.Label>
              <Select
                options={[{ value: '', label: 'Tous' }, ...chantierOptions]}
                value={
                  filterChantierName
                    ? { value: filterChantierName, label: filterChantierName }
                    : { value: '', label: 'Tous' }
                }
                onChange={(opt) => setFilterChantierName(opt?.value || '')}
                isSearchable
                placeholder="🔎 Chercher chantier..."
              />
            </Col>

            <Col md={3}>
              <Form.Label className="fw-bold" style={{ color: '#6d4e36' }}>
                🏢 Société
              </Form.Label>
              <Select
                isMulti
                options={societeOptions}
                value={societeOptions.filter((opt) => filterSociete.includes(opt.value))}
                onChange={(opts) => setFilterSociete(opts ? opts.map((o) => o.value) : [])}
                placeholder="🔎 Filtrer par société..."
              />
            </Col>

            <Col md={2}>
              <Form.Label className="fw-bold" style={{ color: '#6d4e36' }}>
                🚦 Statut
              </Form.Label>
              <Select
                options={statutOptions}
                value={statutOptions.find((x) => x.value === filterStatut) || statutOptions[0]}
                onChange={(opt) => setFilterStatut(opt?.value || '')}
                isSearchable={false}
              />
            </Col>

            <Col md={2}>
              <Form.Label className="fw-bold" style={{ color: '#6d4e36' }}>
                🧭 Mode
              </Form.Label>
              <Select
                options={modeOptions}
                value={modeOptions.find((x) => x.value === filterModeCompteur) || modeOptions[0]}
                onChange={(opt) => setFilterModeCompteur(opt?.value || '')}
                isSearchable={false}
              />
            </Col>

            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => {
                  setFilterChantierName('');
                  setFilterStatut('');
                  setFilterModeCompteur('');
                  setFilterSociete(['RUTAS', 'PISTAS', 'CADEX']);
                }}
              >
                <FaSearch className="me-2" />
                Reset
              </Button>
            </Col>
          </Row>

          <div className="mt-3 vd-table-wrap">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <div className="text-muted mt-2">Chargement…</div>
              </div>
            ) : (
              <TableComponentVidange rows={suiviFiltered} />
            )}
          </div>

          <Row className="mt-4">
            <Col md={12}>
              <Card className="vd-card">
                <Card.Body>
                  <h5 className="vd-title mb-2">
                    <FaHistory className="me-2" />
                    Historique des Vidanges
                  </h5>

                  <Row className="g-2">
                    <Col md={3}>
                      <Form.Label>📅 Du</Form.Label>
                      <Form.Control
                        type="date"
                        value={histStart}
                        onChange={(e) => setHistStart(e.target.value)}
                      />
                    </Col>

                    <Col md={3}>
                      <Form.Label>📅 Au</Form.Label>
                      <Form.Control
                        type="date"
                        value={histEnd}
                        onChange={(e) => setHistEnd(e.target.value)}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>🚜 Matériel</Form.Label>
                      <Select
                        options={[{ value: null, label: 'Tous' }, ...materielOptions]}
                        value={
                          histMaterielId
                            ? materielOptions.find((x) => x.value === histMaterielId) || null
                            : { value: null, label: 'Tous' }
                        }
                        onChange={(opt) => setHistMaterielId(opt?.value || null)}
                        isSearchable
                        placeholder="🔎 Rechercher matériel…"
                      />
                    </Col>

                    <Col md={2} className="d-flex align-items-end">
                      <Button
                        variant="primary"
                        className="w-100"
                        onClick={loadHistorique}
                        disabled={loadingHist}
                      >
                        {loadingHist ? '...' : 'Charger'}
                      </Button>
                    </Col>
                  </Row>

                  <div className="mt-3 vd-table-wrap">
                    <VidangeHistoriqueTable rows={historique} user={user} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#f5f5db', borderBottom: '1px solid #6d4e36' }}
        >
          <Modal.Title style={{ color: '#6d4e36', fontWeight: 900 }}>
            ➕ Nouvelle Vidange
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>🚜 Matériel</Form.Label>
            <Select
              options={materielOptions}
              value={materielOptions.find((x) => x.value === addMaterielId) || null}
              onChange={(opt) => setAddMaterielId(opt?.value || null)}
              isSearchable
              placeholder="Choisir un matériel…"
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>📅 Date vidange</Form.Label>
            <Form.Control
              type="date"
              value={addDate}
              onChange={(e) => setAddDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>📝 Observation</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={addObs}
              onChange={(e) => setAddObs(e.target.value)}
              placeholder="Ex: Vidange + filtre + contrôle niveaux…"
            />
          </Form.Group>

          <Alert variant="warning" className="mt-2">
            ℹ️ Cette action enregistre l’historique + remet le <b>cycle actif</b> à <b>0</b>
            {' '}selon le mode du matériel (<b>heures</b> ou <b>kilomètres</b>).
          </Alert>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>
            Annuler
          </Button>
          <Button variant="success" onClick={handleCreateVidange} disabled={loading}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showKpiModal}
        onHide={() => setShowKpiModal(false)}
        centered
        size="xl"
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#f5f5db', borderBottom: '1px solid #6d4e36' }}
        >
          <Modal.Title style={{ color: '#6d4e36', fontWeight: 900 }}>
            {kpiModalTitle}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="info">
            ℹ️ Résultat basé sur les <b>filtres actuels</b> de la page.
          </Alert>

          <div className="d-flex justify-content-end mb-3">
            <Button variant="success" onClick={exportKpiModalExcel}>
              <FaFileExcel className="me-2" />
              Télécharger Excel
            </Button>
          </div>

          <div className="table-responsive">
            <Table bordered hover striped className="align-middle">
              <thead>
                <tr>
                  <th>🆔 ID</th>
                  <th>🚜 Matricule</th>
                  <th>🏷️ Type</th>
                  <th>🏢 Société</th>
                  <th>🧭 Mode</th>
                  <th>🏗️ Dernier chantier</th>
                  <th>📅 Dernière vidange</th>
                  <th>🔁 Cycle</th>
                  <th>📏 Intervalle</th>
                  <th>⏳ Reste</th>
                </tr>
              </thead>
              <tbody>
                {kpiModalRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-4">
                      ℹ️ Aucun matériel à afficher.
                    </td>
                  </tr>
                ) : (
                  kpiModalRows.map((row) => {
                    const mode = normalizeModeCompteur(row.mode_compteur);
                    return (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.matricule || '—'}</td>
                        <td>{row.type || '—'}</td>
                        <td>{row.societe || '—'}</td>
                        <td>
                          {mode === 'km' ? (
                            <Badge bg="primary"><FaRoad className="me-1" />KM</Badge>
                          ) : (
                            <Badge bg="info" text="dark"><FaClock className="me-1" />Heure</Badge>
                          )}
                        </td>
                        <td>{row.chantier_actuel || '—'}</td>
                        <td>{formatDateDisplay(row.date_vidange)}</td>
                        <td>{formatCompteurDisplay(row.cycle_value, mode)}</td>
                        <td>{formatCompteurDisplay(row.interval_value, mode)}</td>
                        <td>{formatCompteurDisplay(row.reste_value, mode)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="me-auto text-muted">
            Total affiché : <b>{kpiModalRows.length}</b>
          </div>
          <Button variant="secondary" onClick={() => setShowKpiModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showRecalcConfirm}
        title="Recalculer les compteurs"
        message="Recalculer tous les compteurs vidange ? Cette opération peut prendre quelques instants."
        confirmLabel="Recalculer"
        confirmVariant="warning"
        onConfirm={() => { setShowRecalcConfirm(false); handleRecalculate(); }}
        onCancel={() => setShowRecalcConfirm(false)}
      />
    </div>
  );
};

export default VidangePage;